'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function VolumeCalculator() {
  const t = useTranslations("tools.volumeCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Shape states
  const [sphereInputs, setSphereInputs] = useState({ radius: '', unit: 'ft' });
  const [sphereResult, setSphereResult] = useState<string | null>(null);
  const [sphereError, setSphereError] = useState('');

  const [coneInputs, setConeInputs] = useState({ radius: '', height: '', radiusUnit: 'ft', heightUnit: 'ft' });
  const [coneResult, setConeResult] = useState<string | null>(null);
  const [coneError, setConeError] = useState('');

  const [cubeInputs, setCubeInputs] = useState({ edge: '', unit: 'ft' });
  const [cubeResult, setCubeResult] = useState<string | null>(null);
  const [cubeError, setCubeError] = useState('');

  const [cylinderInputs, setCylinderInputs] = useState({ radius: '', height: '', radiusUnit: 'ft', heightUnit: 'ft' });
  const [cylinderResult, setCylinderResult] = useState<string | null>(null);
  const [cylinderError, setCylinderError] = useState('');

  const [rectangularInputs, setRectangularInputs] = useState({ length: '', width: '', height: '', lengthUnit: 'ft', widthUnit: 'ft', heightUnit: 'ft' });
  const [rectangularResult, setRectangularResult] = useState<string | null>(null);
  const [rectangularError, setRectangularError] = useState('');

  const [capsuleInputs, setCapsuleInputs] = useState({ radius: '', height: '', radiusUnit: 'ft', heightUnit: 'ft' });
  const [capsuleResult, setCapsuleResult] = useState<string | null>(null);
  const [capsuleError, setCapsuleError] = useState('');

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const unitConversions: { [key: string]: number } = {
    'm': 1,
    'cm': 0.01,
    'mm': 0.001,
    'ft': 0.3048,
    'in': 0.0254,
    'yd': 0.9144,
    'km': 1000
  };

  const volumeUnits: { [key: string]: string } = {
    'm': 'm³',
    'cm': 'cm³',
    'mm': 'mm³',
    'ft': 'ft³',
    'in': 'in³',
    'yd': 'yd³',
    'km': 'km³'
  };

  const convertToMeters = (value: number, unit: string): number => {
    return value * unitConversions[unit];
  };

  const convertFromCubicMeters = (value: number, unit: string): number => {
    const conversion = unitConversions[unit];
    return value / Math.pow(conversion, 3);
  };

  const formatNumber = (num: number): string => {
    if (num >= 1000000) {
      return num.toExponential(4);
    }
    return num.toFixed(6).replace(/\.?0+$/, '');
  };

  const handleSphereCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    const radius = parseFloat(sphereInputs.radius);

    if (!sphereInputs.radius || radius <= 0) {
      setSphereError(t('errorPositive'));
      setSphereResult(null);
      return;
    }

    const r = convertToMeters(radius, sphereInputs.unit);
    const volume = (4/3) * Math.PI * Math.pow(r, 3);
    const convertedVolume = convertFromCubicMeters(volume, sphereInputs.unit);

    setSphereResult(formatNumber(convertedVolume) + ' ' + volumeUnits[sphereInputs.unit]);
    setSphereError('');
  };

  const handleConeCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    const radius = parseFloat(coneInputs.radius);
    const height = parseFloat(coneInputs.height);

    if (!coneInputs.radius || !coneInputs.height || radius <= 0 || height <= 0) {
      setConeError(t('errorAllPositive'));
      setConeResult(null);
      return;
    }

    const r = convertToMeters(radius, coneInputs.radiusUnit);
    const h = convertToMeters(height, coneInputs.heightUnit);
    const volume = (1/3) * Math.PI * Math.pow(r, 2) * h;
    const convertedVolume = convertFromCubicMeters(volume, coneInputs.radiusUnit);

    setConeResult(formatNumber(convertedVolume) + ' ' + volumeUnits[coneInputs.radiusUnit]);
    setConeError('');
  };

  const handleCubeCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    const edge = parseFloat(cubeInputs.edge);

    if (!cubeInputs.edge || edge <= 0) {
      setCubeError(t('errorPositive'));
      setCubeResult(null);
      return;
    }

    const a = convertToMeters(edge, cubeInputs.unit);
    const volume = Math.pow(a, 3);
    const convertedVolume = convertFromCubicMeters(volume, cubeInputs.unit);

    setCubeResult(formatNumber(convertedVolume) + ' ' + volumeUnits[cubeInputs.unit]);
    setCubeError('');
  };

  const handleCylinderCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    const radius = parseFloat(cylinderInputs.radius);
    const height = parseFloat(cylinderInputs.height);

    if (!cylinderInputs.radius || !cylinderInputs.height || radius <= 0 || height <= 0) {
      setCylinderError(t('errorAllPositive'));
      setCylinderResult(null);
      return;
    }

    const r = convertToMeters(radius, cylinderInputs.radiusUnit);
    const h = convertToMeters(height, cylinderInputs.heightUnit);
    const volume = Math.PI * Math.pow(r, 2) * h;
    const convertedVolume = convertFromCubicMeters(volume, cylinderInputs.radiusUnit);

    setCylinderResult(formatNumber(convertedVolume) + ' ' + volumeUnits[cylinderInputs.radiusUnit]);
    setCylinderError('');
  };

  const handleRectangularCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    const length = parseFloat(rectangularInputs.length);
    const width = parseFloat(rectangularInputs.width);
    const height = parseFloat(rectangularInputs.height);

    if (!rectangularInputs.length || !rectangularInputs.width || !rectangularInputs.height || length <= 0 || width <= 0 || height <= 0) {
      setRectangularError(t('errorAllPositive'));
      setRectangularResult(null);
      return;
    }

    const l = convertToMeters(length, rectangularInputs.lengthUnit);
    const w = convertToMeters(width, rectangularInputs.widthUnit);
    const h = convertToMeters(height, rectangularInputs.heightUnit);
    const volume = l * w * h;
    const convertedVolume = convertFromCubicMeters(volume, rectangularInputs.lengthUnit);

    setRectangularResult(formatNumber(convertedVolume) + ' ' + volumeUnits[rectangularInputs.lengthUnit]);
    setRectangularError('');
  };

  const handleCapsuleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    const radius = parseFloat(capsuleInputs.radius);
    const height = parseFloat(capsuleInputs.height);

    if (!capsuleInputs.radius || !capsuleInputs.height || radius <= 0 || height <= 0) {
      setCapsuleError(t('errorAllPositive'));
      setCapsuleResult(null);
      return;
    }

    const r = convertToMeters(radius, capsuleInputs.radiusUnit);
    const h = convertToMeters(height, capsuleInputs.heightUnit);
    const volume = Math.PI * Math.pow(r, 2) * h + (4/3) * Math.PI * Math.pow(r, 3);
    const convertedVolume = convertFromCubicMeters(volume, capsuleInputs.radiusUnit);

    setCapsuleResult(formatNumber(convertedVolume) + ' ' + volumeUnits[capsuleInputs.radiusUnit]);
    setCapsuleError('');
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '0 1.5rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    { marginBottom: '2rem' },
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, marginBottom: '0' },
    { fontSize: '1rem', lineHeight: 1.6 },
    { fontSize: '1.125rem', lineHeight: 1.7 },
    { fontSize: '1.25rem', lineHeight: 1.8 }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
      borderRadius: '1rem',
      marginBottom: '2rem'
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 1.5rem', borderRadius: '1.5rem' }
  );

  const toolHeaderStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    { marginBottom: '1.5rem' },
    { marginBottom: '2rem' },
    { marginBottom: '2.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const toolDescriptionStyle = getResponsiveStyle(
    { color: colors.textSecondary, marginBottom: '0' },
    { fontSize: '0.875rem' },
    { fontSize: '1rem' },
    { fontSize: '1rem' }
  );

  const shapesGridStyle = getResponsiveStyle(
    {
      display: 'grid',
      gap: '1.5rem',
      marginBottom: '2rem'
    },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: 'repeat(2, 1fr)' },
    { gridTemplateColumns: 'repeat(3, 1fr)' }
  );

  const shapeCardStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      transition: 'transform 0.2s, box-shadow 0.2s'
    },
    { padding: '1.25rem' },
    { padding: '1.5rem' },
    { padding: '1.5rem' }
  );

  const shapeHeaderStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    marginBottom: '1.5rem',
    paddingBottom: '1rem',
    borderBottom: '2px solid #e2e8f0'
  };

  const shapeIconStyle = getResponsiveStyle(
    {},
    { fontSize: '2rem' },
    { fontSize: '2.25rem' },
    { fontSize: '2.5rem' }
  );

  const shapeTitleStyle = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, margin: '0' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.25rem' }
  );

  const formGroupStyle: React.CSSProperties = {
    marginBottom: '1rem'
  };

  const labelStyle = getResponsiveStyle(
    { display: 'block', fontWeight: 500, color: '#475569', marginBottom: '0.5rem' },
    { fontSize: '0.8125rem' },
    { fontSize: '0.875rem' },
    { fontSize: '0.875rem' }
  );

  const inputRowStyle: React.CSSProperties = {
    display: 'flex',
    gap: '0.5rem',
    alignItems: 'center'
  };

  const inputStyle = getResponsiveStyle(
    {
      flex: 1,
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      transition: 'border-color 0.2s, box-shadow 0.2s'
    },
    { padding: '0.5rem 0.75rem', fontSize: '0.875rem' },
    { padding: '0.625rem 0.875rem', fontSize: '0.9375rem' },
    { padding: '0.625rem 0.875rem', fontSize: '0.9375rem' }
  );

  const selectStyle = getResponsiveStyle(
    {
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      background: 'white',
      cursor: 'pointer',
      transition: 'border-color 0.2s'
    },
    { padding: '0.5rem 0.75rem', fontSize: '0.8125rem' },
    { padding: '0.625rem 0.875rem', fontSize: '0.875rem' },
    { padding: '0.625rem 0.875rem', fontSize: '0.875rem' }
  );

  const calculateBtnStyle = getResponsiveStyle(
    {
      width: '100%',
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'transform 0.2s, box-shadow 0.2s',
      marginTop: '0.5rem'
    },
    { padding: '0.625rem 1.25rem', fontSize: '0.9375rem' },
    { padding: '0.75rem 1.5rem', fontSize: '1rem' },
    { padding: '0.75rem 1.5rem', fontSize: '1rem' }
  );

  const resultBoxStyle = getResponsiveStyle(
    {
      marginTop: '1rem',
      background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
      borderRadius: '0.5rem',
      borderLeft: '4px solid #22c55e'
    },
    { padding: '0.875rem' },
    { padding: '1rem' },
    { padding: '1rem' }
  );

  const resultLabelStyle = getResponsiveStyle(
    { color: '#166534', fontWeight: 500, display: 'block', marginBottom: '0.25rem' },
    { fontSize: '0.8125rem' },
    { fontSize: '0.875rem' },
    { fontSize: '0.875rem' }
  );

  const resultValueStyle = getResponsiveStyle(
    { color: '#15803d', fontWeight: 700 },
    { fontSize: '1.25rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.5rem' }
  );

  const errorMessageStyle = getResponsiveStyle(
    { color: '#dc2626', marginTop: '0.5rem' },
    { fontSize: '0.8125rem' },
    { fontSize: '0.875rem' },
    { fontSize: '0.875rem' }
  );

  const formulaHintStyle = getResponsiveStyle(
    { color: '#64748b', fontStyle: 'italic', marginTop: '0.5rem' },
    { fontSize: '0.6875rem' },
    { fontSize: '0.75rem' },
    { fontSize: '0.75rem' }
  );

  const glassCardStyle = getResponsiveStyle(
    {
      background: 'rgba(255, 255, 255, 0.6)',
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: '1px solid rgba(255, 200, 180, 0.2)',
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem'
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2.5rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { color: colors.textSecondary, marginBottom: '1rem', lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem', lineHeight: 1.7 },
    { fontSize: '1.125rem', lineHeight: 1.8 }
  );

  const listStyle = getResponsiveStyle(
    { color: colors.textSecondary, marginLeft: '1.5rem', marginBottom: '1rem' },
    { fontSize: '1rem', lineHeight: 1.6 },
    { fontSize: '1.125rem', lineHeight: 1.7 },
    { fontSize: '1.125rem', lineHeight: 1.8 }
  );

  const listItemStyle: React.CSSProperties = {
    marginBottom: '0.75rem'
  };

  const h3Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.75rem', marginTop: '1.5rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.375rem' }
  );

  const faqItemStyle: React.CSSProperties = {
    marginBottom: '1.5rem'
  };

  const faqQuestionStyle = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const faqAnswerStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '0.9375rem' },
    { fontSize: '1rem' },
    { fontSize: '1rem' }
  );

  const referenceStyle = getResponsiveStyle(
    { color: colors.textSecondary, marginBottom: '0.5rem', lineHeight: 1.6 },
    { fontSize: '0.875rem' },
    { fontSize: '0.9375rem' },
    { fontSize: '0.9375rem' }
  );

  const linkStyle: React.CSSProperties = {
    color: colors.primary,
    textDecoration: 'none',
    transition: 'color 0.2s'
  };

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolHeaderStyle}>
          <h2 style={h2Style}>{t('toolTitle')}</h2>
          <p style={toolDescriptionStyle}>{t('toolDescription')}</p>
        </div>

        <div style={shapesGridStyle}>
          <div style={shapeCardStyle}>
            <div style={shapeHeaderStyle}>
              <span style={shapeIconStyle}>⚪</span>
              <h3 style={shapeTitleStyle}>{t('sphereTitle')}</h3>
            </div>
            <form onSubmit={handleSphereCalculate}>
              <div style={formGroupStyle}>
                <label style={labelStyle}>{t('sphereRadiusLabel')}</label>
                <div style={inputRowStyle}>
                  <input
                    type="number"
                    step="any"
                    min="0"
                    value={sphereInputs.radius}
                    onChange={(e) => setSphereInputs({...sphereInputs, radius: e.target.value})}
                    style={inputStyle}
                    required
                  />
                  <select
                    value={sphereInputs.unit}
                    onChange={(e) => setSphereInputs({...sphereInputs, unit: e.target.value})}
                    style={selectStyle}
                  >
                    <option value="m">{t('unitMeters')}</option>
                    <option value="cm">{t('unitCentimeters')}</option>
                    <option value="mm">{t('unitMillimeters')}</option>
                    <option value="ft">{t('unitFeet')}</option>
                    <option value="in">{t('unitInches')}</option>
                  </select>
                </div>
              </div>
              <div style={formulaHintStyle}>{t('sphereFormula')}</div>
              <button type="submit" style={calculateBtnStyle}>{t('calculateButton')}</button>
              {sphereResult && (
                <div style={resultBoxStyle}>
                  <span style={resultLabelStyle}>{t('volumeLabel')}</span>
                  <span style={resultValueStyle}>{sphereResult}</span>
                </div>
              )}
              {sphereError && <div style={errorMessageStyle}>{sphereError}</div>}
            </form>
          </div>

          <div style={shapeCardStyle}>
            <div style={shapeHeaderStyle}>
              <span style={shapeIconStyle}>🔺</span>
              <h3 style={shapeTitleStyle}>{t('coneTitle')}</h3>
            </div>
            <form onSubmit={handleConeCalculate}>
              <div style={formGroupStyle}>
                <label style={labelStyle}>{t('coneRadiusLabel')}</label>
                <div style={inputRowStyle}>
                  <input
                    type="number"
                    step="any"
                    min="0"
                    value={coneInputs.radius}
                    onChange={(e) => setConeInputs({...coneInputs, radius: e.target.value})}
                    style={inputStyle}
                    required
                  />
                  <select
                    value={coneInputs.radiusUnit}
                    onChange={(e) => setConeInputs({...coneInputs, radiusUnit: e.target.value})}
                    style={selectStyle}
                  >
                    <option value="m">{t('unitMeters')}</option>
                    <option value="cm">{t('unitCentimeters')}</option>
                    <option value="mm">{t('unitMillimeters')}</option>
                    <option value="ft">{t('unitFeet')}</option>
                    <option value="in">{t('unitInches')}</option>
                  </select>
                </div>
              </div>
              <div style={formGroupStyle}>
                <label style={labelStyle}>{t('coneHeightLabel')}</label>
                <div style={inputRowStyle}>
                  <input
                    type="number"
                    step="any"
                    min="0"
                    value={coneInputs.height}
                    onChange={(e) => setConeInputs({...coneInputs, height: e.target.value})}
                    style={inputStyle}
                    required
                  />
                  <select
                    value={coneInputs.heightUnit}
                    onChange={(e) => setConeInputs({...coneInputs, heightUnit: e.target.value})}
                    style={selectStyle}
                  >
                    <option value="m">{t('unitMeters')}</option>
                    <option value="cm">{t('unitCentimeters')}</option>
                    <option value="mm">{t('unitMillimeters')}</option>
                    <option value="ft">{t('unitFeet')}</option>
                    <option value="in">{t('unitInches')}</option>
                  </select>
                </div>
              </div>
              <div style={formulaHintStyle}>{t('coneFormula')}</div>
              <button type="submit" style={calculateBtnStyle}>{t('calculateButton')}</button>
              {coneResult && (
                <div style={resultBoxStyle}>
                  <span style={resultLabelStyle}>{t('volumeLabel')}</span>
                  <span style={resultValueStyle}>{coneResult}</span>
                </div>
              )}
              {coneError && <div style={errorMessageStyle}>{coneError}</div>}
            </form>
          </div>

          <div style={shapeCardStyle}>
            <div style={shapeHeaderStyle}>
              <span style={shapeIconStyle}>🟦</span>
              <h3 style={shapeTitleStyle}>{t('cubeTitle')}</h3>
            </div>
            <form onSubmit={handleCubeCalculate}>
              <div style={formGroupStyle}>
                <label style={labelStyle}>{t('cubeEdgeLabel')}</label>
                <div style={inputRowStyle}>
                  <input
                    type="number"
                    step="any"
                    min="0"
                    value={cubeInputs.edge}
                    onChange={(e) => setCubeInputs({...cubeInputs, edge: e.target.value})}
                    style={inputStyle}
                    required
                  />
                  <select
                    value={cubeInputs.unit}
                    onChange={(e) => setCubeInputs({...cubeInputs, unit: e.target.value})}
                    style={selectStyle}
                  >
                    <option value="m">{t('unitMeters')}</option>
                    <option value="cm">{t('unitCentimeters')}</option>
                    <option value="mm">{t('unitMillimeters')}</option>
                    <option value="ft">{t('unitFeet')}</option>
                    <option value="in">{t('unitInches')}</option>
                  </select>
                </div>
              </div>
              <div style={formulaHintStyle}>{t('cubeFormula')}</div>
              <button type="submit" style={calculateBtnStyle}>{t('calculateButton')}</button>
              {cubeResult && (
                <div style={resultBoxStyle}>
                  <span style={resultLabelStyle}>{t('volumeLabel')}</span>
                  <span style={resultValueStyle}>{cubeResult}</span>
                </div>
              )}
              {cubeError && <div style={errorMessageStyle}>{cubeError}</div>}
            </form>
          </div>

          <div style={shapeCardStyle}>
            <div style={shapeHeaderStyle}>
              <span style={shapeIconStyle}>🥫</span>
              <h3 style={shapeTitleStyle}>{t('cylinderTitle')}</h3>
            </div>
            <form onSubmit={handleCylinderCalculate}>
              <div style={formGroupStyle}>
                <label style={labelStyle}>{t('cylinderRadiusLabel')}</label>
                <div style={inputRowStyle}>
                  <input
                    type="number"
                    step="any"
                    min="0"
                    value={cylinderInputs.radius}
                    onChange={(e) => setCylinderInputs({...cylinderInputs, radius: e.target.value})}
                    style={inputStyle}
                    required
                  />
                  <select
                    value={cylinderInputs.radiusUnit}
                    onChange={(e) => setCylinderInputs({...cylinderInputs, radiusUnit: e.target.value})}
                    style={selectStyle}
                  >
                    <option value="m">{t('unitMeters')}</option>
                    <option value="cm">{t('unitCentimeters')}</option>
                    <option value="mm">{t('unitMillimeters')}</option>
                    <option value="ft">{t('unitFeet')}</option>
                    <option value="in">{t('unitInches')}</option>
                  </select>
                </div>
              </div>
              <div style={formGroupStyle}>
                <label style={labelStyle}>{t('cylinderHeightLabel')}</label>
                <div style={inputRowStyle}>
                  <input
                    type="number"
                    step="any"
                    min="0"
                    value={cylinderInputs.height}
                    onChange={(e) => setCylinderInputs({...cylinderInputs, height: e.target.value})}
                    style={inputStyle}
                    required
                  />
                  <select
                    value={cylinderInputs.heightUnit}
                    onChange={(e) => setCylinderInputs({...cylinderInputs, heightUnit: e.target.value})}
                    style={selectStyle}
                  >
                    <option value="m">{t('unitMeters')}</option>
                    <option value="cm">{t('unitCentimeters')}</option>
                    <option value="mm">{t('unitMillimeters')}</option>
                    <option value="ft">{t('unitFeet')}</option>
                    <option value="in">{t('unitInches')}</option>
                  </select>
                </div>
              </div>
              <div style={formulaHintStyle}>{t('cylinderFormula')}</div>
              <button type="submit" style={calculateBtnStyle}>{t('calculateButton')}</button>
              {cylinderResult && (
                <div style={resultBoxStyle}>
                  <span style={resultLabelStyle}>{t('volumeLabel')}</span>
                  <span style={resultValueStyle}>{cylinderResult}</span>
                </div>
              )}
              {cylinderError && <div style={errorMessageStyle}>{cylinderError}</div>}
            </form>
          </div>

          <div style={shapeCardStyle}>
            <div style={shapeHeaderStyle}>
              <span style={shapeIconStyle}>📦</span>
              <h3 style={shapeTitleStyle}>{t('rectangularTitle')}</h3>
            </div>
            <form onSubmit={handleRectangularCalculate}>
              <div style={formGroupStyle}>
                <label style={labelStyle}>{t('rectangularLengthLabel')}</label>
                <div style={inputRowStyle}>
                  <input
                    type="number"
                    step="any"
                    min="0"
                    value={rectangularInputs.length}
                    onChange={(e) => setRectangularInputs({...rectangularInputs, length: e.target.value})}
                    style={inputStyle}
                    required
                  />
                  <select
                    value={rectangularInputs.lengthUnit}
                    onChange={(e) => setRectangularInputs({...rectangularInputs, lengthUnit: e.target.value})}
                    style={selectStyle}
                  >
                    <option value="m">{t('unitMeters')}</option>
                    <option value="cm">{t('unitCentimeters')}</option>
                    <option value="mm">{t('unitMillimeters')}</option>
                    <option value="ft">{t('unitFeet')}</option>
                    <option value="in">{t('unitInches')}</option>
                  </select>
                </div>
              </div>
              <div style={formGroupStyle}>
                <label style={labelStyle}>{t('rectangularWidthLabel')}</label>
                <div style={inputRowStyle}>
                  <input
                    type="number"
                    step="any"
                    min="0"
                    value={rectangularInputs.width}
                    onChange={(e) => setRectangularInputs({...rectangularInputs, width: e.target.value})}
                    style={inputStyle}
                    required
                  />
                  <select
                    value={rectangularInputs.widthUnit}
                    onChange={(e) => setRectangularInputs({...rectangularInputs, widthUnit: e.target.value})}
                    style={selectStyle}
                  >
                    <option value="m">{t('unitMeters')}</option>
                    <option value="cm">{t('unitCentimeters')}</option>
                    <option value="mm">{t('unitMillimeters')}</option>
                    <option value="ft">{t('unitFeet')}</option>
                    <option value="in">{t('unitInches')}</option>
                  </select>
                </div>
              </div>
              <div style={formGroupStyle}>
                <label style={labelStyle}>{t('rectangularHeightLabel')}</label>
                <div style={inputRowStyle}>
                  <input
                    type="number"
                    step="any"
                    min="0"
                    value={rectangularInputs.height}
                    onChange={(e) => setRectangularInputs({...rectangularInputs, height: e.target.value})}
                    style={inputStyle}
                    required
                  />
                  <select
                    value={rectangularInputs.heightUnit}
                    onChange={(e) => setRectangularInputs({...rectangularInputs, heightUnit: e.target.value})}
                    style={selectStyle}
                  >
                    <option value="m">{t('unitMeters')}</option>
                    <option value="cm">{t('unitCentimeters')}</option>
                    <option value="mm">{t('unitMillimeters')}</option>
                    <option value="ft">{t('unitFeet')}</option>
                    <option value="in">{t('unitInches')}</option>
                  </select>
                </div>
              </div>
              <div style={formulaHintStyle}>{t('rectangularFormula')}</div>
              <button type="submit" style={calculateBtnStyle}>{t('calculateButton')}</button>
              {rectangularResult && (
                <div style={resultBoxStyle}>
                  <span style={resultLabelStyle}>{t('volumeLabel')}</span>
                  <span style={resultValueStyle}>{rectangularResult}</span>
                </div>
              )}
              {rectangularError && <div style={errorMessageStyle}>{rectangularError}</div>}
            </form>
          </div>

          <div style={shapeCardStyle}>
            <div style={shapeHeaderStyle}>
              <span style={shapeIconStyle}>💊</span>
              <h3 style={shapeTitleStyle}>{t('capsuleTitle')}</h3>
            </div>
            <form onSubmit={handleCapsuleCalculate}>
              <div style={formGroupStyle}>
                <label style={labelStyle}>{t('capsuleRadiusLabel')}</label>
                <div style={inputRowStyle}>
                  <input
                    type="number"
                    step="any"
                    min="0"
                    value={capsuleInputs.radius}
                    onChange={(e) => setCapsuleInputs({...capsuleInputs, radius: e.target.value})}
                    style={inputStyle}
                    required
                  />
                  <select
                    value={capsuleInputs.radiusUnit}
                    onChange={(e) => setCapsuleInputs({...capsuleInputs, radiusUnit: e.target.value})}
                    style={selectStyle}
                  >
                    <option value="m">{t('unitMeters')}</option>
                    <option value="cm">{t('unitCentimeters')}</option>
                    <option value="mm">{t('unitMillimeters')}</option>
                    <option value="ft">{t('unitFeet')}</option>
                    <option value="in">{t('unitInches')}</option>
                  </select>
                </div>
              </div>
              <div style={formGroupStyle}>
                <label style={labelStyle}>{t('capsuleHeightLabel')}</label>
                <div style={inputRowStyle}>
                  <input
                    type="number"
                    step="any"
                    min="0"
                    value={capsuleInputs.height}
                    onChange={(e) => setCapsuleInputs({...capsuleInputs, height: e.target.value})}
                    style={inputStyle}
                    required
                  />
                  <select
                    value={capsuleInputs.heightUnit}
                    onChange={(e) => setCapsuleInputs({...capsuleInputs, heightUnit: e.target.value})}
                    style={selectStyle}
                  >
                    <option value="m">{t('unitMeters')}</option>
                    <option value="cm">{t('unitCentimeters')}</option>
                    <option value="mm">{t('unitMillimeters')}</option>
                    <option value="ft">{t('unitFeet')}</option>
                    <option value="in">{t('unitInches')}</option>
                  </select>
                </div>
              </div>
              <div style={formulaHintStyle}>{t('capsuleFormula')}</div>
              <button type="submit" style={calculateBtnStyle}>{t('calculateButton')}</button>
              {capsuleResult && (
                <div style={resultBoxStyle}>
                  <span style={resultLabelStyle}>{t('volumeLabel')}</span>
                  <span style={resultValueStyle}>{capsuleResult}</span>
                </div>
              )}
              {capsuleError && <div style={errorMessageStyle}>{capsuleError}</div>}
            </form>
          </div>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introParagraph1')}</p>
        <p style={paragraphStyle}>{t('introParagraph2')}</p>
        <p style={paragraphStyle}>{t('introParagraph3')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}>{t('howToStep1')}</li>
          <li style={listItemStyle}>{t('howToStep2')}</li>
          <li style={listItemStyle}>{t('howToStep3')}</li>
          <li style={listItemStyle}>{t('howToStep4')}</li>
          <li style={listItemStyle}>{t('howToStep5')}</li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>

        <h3 style={h3Style}>{t('insightAccuracyTitle')}</h3>
        <p style={paragraphStyle}>{t('insightAccuracyText')}</p>

        <h3 style={h3Style}>{t('insightCostTitle')}</h3>
        <p style={paragraphStyle}>{t('insightCostText')}</p>

        <h3 style={h3Style}>{t('insightTechnologyTitle')}</h3>
        <p style={paragraphStyle}>{t('insightTechnologyText')}</p>

        <h3 style={h3Style}>{t('insightBestPracticesTitle')}</h3>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t('insightBestPractice1')}</li>
          <li style={listItemStyle}>{t('insightBestPractice2')}</li>
          <li style={listItemStyle}>{t('insightBestPractice3')}</li>
          <li style={listItemStyle}>{t('insightBestPractice4')}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('detailsTitle')}</h2>

        <h3 style={h3Style}>{t('detailConstructionTitle')}</h3>
        <p style={paragraphStyle}>{t('detailConstructionText')}</p>

        <h3 style={h3Style}>{t('detailConcreteTitle')}</h3>
        <p style={paragraphStyle}>{t('detailConcreteText')}</p>

        <h3 style={h3Style}>{t('detailEarthworkTitle')}</h3>
        <p style={paragraphStyle}>{t('detailEarthworkText')}</p>

        <h3 style={h3Style}>{t('detailMaterialTitle')}</h3>
        <p style={paragraphStyle}>{t('detailMaterialText')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq1Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq1Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq2Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq2Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq3Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq3Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq4Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq4Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq5Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq5Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq6Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq6Answer')}</p>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <div style={referenceStyle}>
          [1] <a href="https://www.bobthebuilders.uk/blog?title=construction-calculators" style={linkStyle} target="_blank" rel="noopener noreferrer">The Power of Construction Calculators | Bobthebuilders</a>
        </div>
        <div style={referenceStyle}>
          [2] <a href="https://www.youtube.com/watch?v=Q2ZmR3islMg" style={linkStyle} target="_blank" rel="noopener noreferrer">What Are Real-World Applications Of Volume Calculation? (civil engineering video)</a>
        </div>
        <div style={referenceStyle}>
          [3] <a href="https://www.mycemco.com/blog/raw-materials" style={linkStyle} target="_blank" rel="noopener noreferrer">Building Material Calculator: its usages and benefits in construction</a>
        </div>
        <div style={referenceStyle}>
          [4] <a href="https://www.aparnarmc.com/importance-of-accurate-volume-calculation-in-rmc-production-for-different-shapes-dimensions-etc/" style={linkStyle} target="_blank" rel="noopener noreferrer">Importance of accurate volume calculation in RMC production for different shapes & dimensions</a>
        </div>
        <div style={referenceStyle}>
          [5] <a href="https://www.datumate.com/blog/accurate-construction-volume-calculation/" style={linkStyle} target="_blank" rel="noopener noreferrer">Achieve Accurate Construction Volume Calculation in Easy Steps</a>
        </div>
        <div style={referenceStyle}>
          [6] <a href="https://dilandroandrews.com/using-a-building-material-calculator/" style={linkStyle} target="_blank" rel="noopener noreferrer">Using a Building Material Calculator to Optimize Construction</a>
        </div>
        <div style={referenceStyle}>
          [7] <a href="https://contractorforeman.com/calculate-materials-for-concrete-to-reap-these-7-cost-saving-benefits/" style={linkStyle} target="_blank" rel="noopener noreferrer">Concrete Materials: 7 Cost-Saving Benefits of Accurate Calculation</a>
        </div>
      </section>
    </div>
  );
}